import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight, ArrowLeft, ArrowUpRight, Building2, TrendingUp, Wrench,
  Wallet, Layers, ListChecks, FileDown, Users, Globe, Sparkles,
  Play, ShieldCheck, Calculator, BarChart3,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import DemoDialog from "@/components/DemoDialog";

/**
 * FlipCost — whole-property renovation & flip decision engine.
 *
 * Layout mirrors the SpaceCost landing for visual consistency across the
 * MyPlanCost suite, but the copy, imagery, sample budget and feature set
 * stay FlipCost-specific (ARV, profit, feasibility, whole-property scope).
 *
 * Theme: keeps the default sage-green accent (no `data-theme` override).
 */
const HERO_HOUSE =
  "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=2400&q=80";
const FLIP_CARD =
  "https://images.unsplash.com/photo-1505691938895-1758d7feb511?auto=format&fit=crop&w=1600&q=80";
const SELL_CARD =
  "https://images.unsplash.com/photo-1564013799919-ab600027ffc6?auto=format&fit=crop&w=1600&q=80";
const LIVE_CARD =
  "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?auto=format&fit=crop&w=1600&q=80";

const FEATURES = [
  {
    icon: Calculator,
    title: "Whole-property cost engine",
    body: "Rooms, exterior, systems, soft costs and extensions in one model. Default rates auto-scale to your region — override anything per project.",
  },
  {
    icon: BarChart3,
    title: "Profit, ROI & feasibility",
    body: "Live profit waterfall, ROI, margin and cost-per-sqm benchmarks update as you edit. See instantly whether the deal still works.",
  },
  {
    icon: ListChecks,
    title: "Estimate vs Actual",
    body: "Switch to Actual mode and track every receipt against the plan. Variance front-and-centre — no nasty end-of-build surprises.",
  },
  {
    icon: Layers,
    title: "Extensions: shell & fitout",
    body: "Add an extension or new build with shell costs by sqm and a separate fitout per added room. Construction-aware (brick, wood, steel).",
  },
  {
    icon: Users,
    title: "Share with your team",
    body: "Invite a partner, agent or contractor — view-only or full edit. Email delivery via Resend, no public links, full audit trail.",
  },
  {
    icon: FileDown,
    title: "Investor-ready report",
    body: "One-click PDF: overview, cost breakdown, ARV vs total cost, feasibility status and an editable offer-justification line for the seller.",
  },
];

const USE_CASES = [
  {
    img: FLIP_CARD,
    alt: "Renovated property at sunset",
    eyebrow: "Flip for profit",
    title: "Buy. Renovate. Sell.",
    body: "Test the deal before you buy. Run the numbers across multiple finish levels and decide on a defensible offer.",
  },
  {
    img: SELL_CARD,
    alt: "Modern home interior",
    eyebrow: "Renovate to sell",
    title: "Spend where it actually moves the needle.",
    body: "Prioritise upgrades that lift sale price — kitchens, bathrooms, kerb appeal — and skip the ones buyers won't pay for.",
  },
  {
    img: LIVE_CARD,
    alt: "Family-friendly renovation",
    eyebrow: "Renovate to live",
    title: "Plan your forever home with confidence.",
    body: "Map every cost — rooms, systems, soft costs — so the build budget you commit to is the budget you actually live with.",
  },
];

const FAQS = [
  {
    q: "Who is FlipCost for?",
    a: "Property investors planning a flip, homeowners renovating to sell, and homeowners renovating to live. Anyone who needs the whole-property number — not just one room.",
  },
  {
    q: "What does R280 actually unlock?",
    a: "One full FlipCost project — every tab, every room, every export. Pay once, plan with confidence. Buy more unlocks any time you start another property. Subscriptions (Pro / Unlimited) are launching soon.",
  },
  {
    q: "How is FlipCost different from SpaceCost?",
    a: "FlipCost is whole-property — exterior, systems, soft costs, profit, ARV and feasibility. SpaceCost is single-space — kitchen refit, bathroom remodel, garden build. Use FlipCost when you're costing a whole project; use SpaceCost when you only care about one space.",
  },
  {
    q: "Are the rates accurate for my region?",
    a: "Defaults are smart starting points scaled to your currency. Every line item, room rate and extension cost is editable in the Cost Library — save your local rates per project for full accuracy.",
  },
];

export default function Landing() {
  const navigate = useNavigate();
  const [demoOpen, setDemoOpen] = useState(false);

  useEffect(() => {
    document.title = "FlipCost — Run the numbers before you swing the first hammer";
    return () => { document.title = "MyPlanCost"; };
  }, []);

  return (
    <div
      className="min-h-screen bg-[#FAFAF9] text-[#1C1C1C] fc-fade-in"
      data-testid="landing-page"
    >
      <header className="sticky top-0 z-30 fc-header-bar">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 py-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <button
              data-testid="flipcost-back-myplancost"
              onClick={() => navigate("/")}
              className="inline-flex items-center gap-1.5 text-[12px] text-[#57534E] hover:text-[#1C1C1C] bg-white hover:bg-black/[0.03] border border-black/[0.08] rounded-full h-8 px-3 transition-colors shrink-0"
              aria-label="Back to MyPlanCost"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> <span className="hidden sm:inline">MyPlanCost</span>
            </button>
            <button
              data-testid="landing-home-btn"
              onClick={() => navigate("/flipcost")}
              className="flex items-baseline gap-2.5 sm:gap-3 group shrink-0"
              aria-label="FlipCost by MyPlanCost — home"
            >
              <span className="fc-heading text-2xl sm:text-[28px] font-extrabold tracking-tight text-[#1C1C1C] leading-none">FlipCost</span>
              <span className="hidden sm:inline text-[10px] uppercase tracking-[0.25em] text-[#A8A29E] font-medium leading-none whitespace-nowrap">
                by MyPlanCost
              </span>
            </button>
          </div>
          <nav className="hidden md:flex items-center gap-1 text-[13px] text-[#57534E]">
            <button data-testid="landing-nav-features" onClick={() => document.getElementById("features")?.scrollIntoView({ behavior: "smooth" })} className="px-3 py-2 hover:text-[#1C1C1C]">Features</button>
            <button data-testid="landing-nav-pricing" onClick={() => navigate("/pricing")} className="px-3 py-2 hover:text-[#1C1C1C]">Pricing</button>
            <button data-testid="landing-nav-how" onClick={() => navigate("/how-it-works")} className="px-3 py-2 hover:text-[#1C1C1C]">How it works</button>
            <button data-testid="landing-nav-faq" onClick={() => navigate("/faq")} className="px-3 py-2 hover:text-[#1C1C1C]">FAQ</button>
            <Button
              data-testid="landing-nav-signin"
              onClick={() => navigate("/auth?mode=login")}
              variant="ghost"
              className="text-[#57534E] hover:text-[#1C1C1C] h-9 ml-1"
            >
              Sign in
            </Button>
          </nav>
          <div className="flex items-center gap-2">
            <button
              type="button"
              data-testid="landing-cross-spacecost"
              onClick={() => navigate("/spacecost")}
              className="hidden sm:inline-flex items-center gap-1.5 text-[12px] text-sage-700 hover:text-sage-800 bg-sage-50 hover:bg-sage-100 border border-sage-200 rounded-full h-8 px-3 transition-colors"
              title="Just one room? Try SpaceCost"
            >
              Just one room? Try SpaceCost <ArrowRight className="w-3 h-3" />
            </button>
            {/* Mobile-only Sign in (the nav-bar Sign in is desktop-only). */}
            <Button
              data-testid="landing-nav-signin-mobile"
              onClick={() => navigate("/auth?mode=login")}
              variant="ghost"
              className="md:hidden text-[#57534E] hover:text-[#1C1C1C] h-9"
            >
              Sign in
            </Button>
            <Button
              data-testid="landing-nav-start"
              onClick={() => navigate("/auth?mode=register")}
              className="bg-[#1C1C1C] text-white hover:bg-[#2A2A2E] h-9 px-3 text-sm rounded-lg"
            >
              Start a project <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 py-16 sm:py-24 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-sage-700 bg-sage-100 border border-sage-200 rounded-full px-3 py-1">
              <Sparkles className="w-3 h-3" /> MyPlanCost · Cost planning suite
            </div>
            <h1 className="fc-heading text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight mt-5 leading-[1.05]">
              Run the numbers —<br /> before you swing the first hammer.
            </h1>
            <p className="text-base sm:text-lg text-[#57534E] mt-6 max-w-xl leading-relaxed">
              FlipCost is a whole-property renovation decision engine that uses region-based cost
              averages to automate your budget — helping you understand total costs, potential
              value increase, and overall feasibility in one editable model.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Button
                data-testid="hero-cta-register"
                onClick={() => navigate("/auth?mode=register")}
                className="bg-[#1C1C1C] text-white hover:bg-[#2A2A2E] rounded-lg h-12 px-6 text-[15px] font-medium"
              >
                Start your first project <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
              <Button
                data-testid="hero-cta-howitworks"
                onClick={() => navigate("/how-it-works")}
                variant="ghost"
                className="text-[#57534E] hover:text-[#1C1C1C] h-12 px-4"
              >
                See how it works
              </Button>
              <Button
                data-testid="hero-cta-demo"
                onClick={() => setDemoOpen(true)}
                variant="ghost"
                className="text-sage-700 hover:text-sage-800 h-12 px-4 inline-flex items-center gap-1.5"
              >
                <Play className="w-4 h-4" /> Live demo
              </Button>
            </div>
            <div className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-[12px] text-[#78716C]">
              <span className="inline-flex items-center gap-1.5"><Wallet className="w-3.5 h-3.5 text-sage-700" /> R280 ZAR once-off</span>
              <span className="inline-flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5 text-sage-700" /> Whole-property scope</span>
              <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-sage-700" /> No subscription</span>
            </div>
          </div>
          <div className="relative">
            <div className="absolute -inset-6 bg-gradient-to-br from-sage-100/70 via-transparent to-sage-200/40 rounded-3xl blur-2xl" />
            <div className="relative fc-card p-1.5 overflow-hidden">
              <img
                src={HERO_HOUSE}
                alt="Renovated property exterior"
                className="w-full h-[420px] object-cover rounded-xl"
              />
            </div>
            <div className="absolute -bottom-5 -left-4 sm:left-6 fc-card p-4 sm:p-5 max-w-[280px]">
              <div className="fc-label">Sample feasibility</div>
              <div className="flex items-end gap-2 mt-1">
                <div className="fc-heading text-2xl text-[#1C1C1C]">R1.42m</div>
                <div className="text-[11px] text-[#78716C] mb-1">total project cost</div>
              </div>
              <div className="mt-2 flex items-center gap-3 text-[12px]">
                <span className="text-[#059669] font-medium">+R380k profit</span>
                <span className="text-[#A8A29E]">·</span>
                <span className="text-[#57534E]">26.7% ROI</span>
              </div>
              <div className="text-[11px] text-[#A8A29E] mt-2">3-bed flip · ARV R2.1m</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-16 sm:py-24 bg-white border-y border-black/[0.05]">
        <div className="max-w-6xl mx-auto px-4 sm:px-8">
          <div className="max-w-2xl">
            <div className="fc-label text-sage-700">Tools that earn their keep</div>
            <h2 className="fc-heading text-3xl sm:text-4xl font-medium tracking-tight mt-3 leading-[1.1]">
              Everything you need to cost a whole project — properly.
            </h2>
            <p className="text-base text-[#57534E] mt-4 leading-relaxed">
              Not back-of-napkin. Not guesswork. The full deal — rooms, exterior, systems, extensions,
              soft costs and the profit math that follows.
            </p>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="features-grid">
            {FEATURES.map((f) => (
              <div key={f.title} className="fc-card p-6 hover:border-sage-300/60 transition-colors">
                <div className="w-10 h-10 rounded-lg bg-sage-100 border border-sage-200 grid place-items-center text-sage-700">
                  <f.icon className="w-4 h-4" />
                </div>
                <div className="fc-heading text-[15px] font-medium text-[#1C1C1C] mt-4">{f.title}</div>
                <div className="text-[13px] text-[#57534E] mt-2 leading-relaxed">{f.body}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use cases */}
      <section className="py-16 sm:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-end">
            <div className="lg:col-span-2">
              <div className="fc-label text-sage-700">Built for the deal</div>
              <h2 className="fc-heading text-3xl sm:text-4xl font-medium tracking-tight mt-3 leading-[1.1]">
                Flip it, sell it, live in it — same tool, three lenses.
              </h2>
              <p className="text-base text-[#57534E] mt-4 leading-relaxed">
                Pick a project goal and FlipCost re-frames the dashboard around it — profit-first for flips,
                value-add for sellers, budget-first for owner-builders. Every tab, every metric, every report.
              </p>
              <div className="mt-6">
                <Button
                  data-testid="usecase-cta-pricing"
                  onClick={() => navigate("/pricing")}
                  className="bg-[#1C1C1C] text-white hover:bg-[#2A2A2E] rounded-lg h-11 px-5"
                >
                  See pricing <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
              </div>
            </div>
            <div className="lg:col-span-3 grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="usecase-grid">
              {USE_CASES.map((u) => (
                <div key={u.title} className="fc-card overflow-hidden">
                  <img src={u.img} alt={u.alt} className="w-full h-44 object-cover" />
                  <div className="p-4">
                    <div className="fc-label text-sage-700">{u.eyebrow}</div>
                    <div className="fc-heading text-[14px] text-[#1C1C1C] mt-1.5">{u.title}</div>
                    <div className="text-[12px] text-[#78716C] mt-1.5 leading-relaxed">{u.body}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Mini-FAQ */}
      <section className="py-16 sm:py-20 bg-white border-y border-black/[0.05]">
        <div className="max-w-3xl mx-auto px-4 sm:px-8">
          <div className="fc-label text-sage-700">Quick questions</div>
          <h2 className="fc-heading text-3xl font-medium tracking-tight mt-3">Before you start</h2>
          <div className="mt-8 space-y-4" data-testid="landing-faq">
            {FAQS.map((f) => (
              <details key={f.q} className="fc-card p-5 group">
                <summary className="flex items-center justify-between gap-3 cursor-pointer list-none">
                  <span className="fc-heading text-[14px] text-[#1C1C1C]">{f.q}</span>
                  <span className="text-sage-700 text-xs uppercase tracking-[0.2em]">read</span>
                </summary>
                <p className="text-[13px] text-[#57534E] leading-relaxed mt-3">{f.a}</p>
              </details>
            ))}
          </div>
          <div className="mt-8 text-center">
            <Button
              data-testid="landing-faq-more"
              onClick={() => navigate("/faq")}
              variant="ghost"
              className="text-sage-700 hover:text-sage-800"
            >
              All FAQs <ArrowRight className="w-3.5 h-3.5 ml-1" />
            </Button>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 sm:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-8 text-center">
          <div className="fc-label text-sage-700">R280 ZAR · once-off</div>
          <h2 className="fc-heading text-4xl sm:text-5xl font-medium tracking-tight mt-3 leading-[1.05]">
            Stop guessing. Start costing.
          </h2>
          <p className="text-base text-[#57534E] mt-5 max-w-xl mx-auto leading-relaxed">
            One single payment unlocks one project — every tab, every report, every ARV calc. No subscriptions. Cancel nothing.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Button
              data-testid="cta-bottom-register"
              onClick={() => navigate("/auth?mode=register")}
              className="bg-[#1C1C1C] text-white hover:bg-[#2A2A2E] rounded-lg h-12 px-6 text-[15px] font-medium"
            >
              Get started <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
            <Button
              data-testid="cta-bottom-pricing"
              onClick={() => navigate("/pricing")}
              variant="ghost"
              className="text-[#57534E] hover:text-[#1C1C1C] h-12 px-4"
            >
              View pricing
            </Button>
          </div>
        </div>
      </section>

      <footer className="bg-[#1C1C1C] text-white/70 mt-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 py-12 grid grid-cols-2 md:grid-cols-4 gap-8 text-[13px]">
          <div className="col-span-2">
            <div className="fc-heading text-white text-base">FlipCost</div>
            <div className="text-[12px] mt-2 text-white/55 max-w-sm leading-relaxed">
              Whole-property renovation cost &amp; feasibility from the MyPlanCost suite. FlipCost helps investors and homeowners cost the entire deal — not just one room.
            </div>
            <button
              data-testid="footer-cross-spacecost"
              onClick={() => navigate("/spacecost")}
              className="inline-flex items-center gap-1.5 mt-4 text-sage-300 hover:text-white text-[12px]"
            >
              Just one room? Try SpaceCost <ArrowRight className="w-3 h-3" />
            </button>
          </div>
          <div>
            <div className="text-white text-[12px] uppercase tracking-[0.18em]">Product</div>
            <ul className="mt-4 space-y-2.5">
              <li><button data-testid="footer-features" onClick={() => document.getElementById("features")?.scrollIntoView({ behavior: "smooth" })} className="hover:text-white">Features</button></li>
              <li><button data-testid="footer-pricing" onClick={() => navigate("/pricing")} className="hover:text-white">Pricing</button></li>
              <li><button data-testid="footer-howitworks" onClick={() => navigate("/how-it-works")} className="hover:text-white">How it works</button></li>
              <li><button data-testid="footer-faq" onClick={() => navigate("/faq")} className="hover:text-white">FAQ</button></li>
            </ul>
          </div>
          <div>
            <div className="text-white text-[12px] uppercase tracking-[0.18em]">Suite</div>
            <ul className="mt-4 space-y-2.5">
              <li><button data-testid="footer-myplancost" onClick={() => navigate("/")} className="hover:text-white">MyPlanCost</button></li>
              <li><button data-testid="footer-spacecost" onClick={() => navigate("/spacecost")} className="hover:text-white">SpaceCost</button></li>
              <li><button data-testid="footer-disclaimer" onClick={() => navigate("/disclaimer")} className="hover:text-white">Disclaimer</button></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/10">
          <div className="max-w-6xl mx-auto px-4 sm:px-8 py-5 flex flex-wrap items-center justify-between gap-3 text-[12px] text-white/45">
            <div>© {new Date().getFullYear()} MyPlanCost. All rights reserved.</div>
            <div>Estimates only. See the <button onClick={() => navigate("/disclaimer")} className="underline hover:text-white/80">full disclaimer</button>.</div>
          </div>
        </div>
      </footer>

      <DemoDialog open={demoOpen} onOpenChange={setDemoOpen} />
    </div>
  );
}
